import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Plus, Pencil, Trash2, Tags, Save, Search } from "lucide-react";
import type { PriceList, PriceListWithPrices, Product } from "@shared/schema";

export default function AdminPriceLists() {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingPriceList, setEditingPriceList] = useState<PriceList | null>(null);
  const [pricesDialogPL, setPricesDialogPL] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editName, setEditName] = useState("");
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [priceSearch, setPriceSearch] = useState("");

  const { data: priceLists, isLoading } = useQuery<PriceList[]>({
    queryKey: ["/api/admin/price-lists"],
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/admin/products"],
  });

  const { data: priceListDetail } = useQuery<PriceListWithPrices>({
    queryKey: ["/api/admin/price-lists", pricesDialogPL],
    enabled: !!pricesDialogPL,
  });

  const createMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/price-lists", { name: newName }),
    onSuccess: () => {
      toast({ title: "Prislista skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/price-lists"] });
      setCreateDialogOpen(false);
      setNewName("");
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa prislista", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/admin/price-lists/${editingPriceList?.id}`, { name: editName }),
    onSuccess: () => {
      toast({ title: "Prislista uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/price-lists"] });
      setEditingPriceList(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/price-lists/${id}`),
    onSuccess: () => {
      toast({ title: "Prislista raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/price-lists"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera prislistan", variant: "destructive" });
    },
  });

  const savePricesMutation = useMutation({
    mutationFn: () => {
      const prices = Object.entries(priceEdits).map(([productId, price]) => ({
        productId,
        price: price === "" ? null : price,
      }));
      return apiRequest("PUT", `/api/admin/price-lists/${pricesDialogPL}/prices`, { prices });
    },
    onSuccess: () => {
      toast({ title: "Priser sparade!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/price-lists", pricesDialogPL] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte spara priser", variant: "destructive" });
    },
  });

  const openPricesDialog = (plId: string) => {
    setPricesDialogPL(plId);
    setPriceEdits({});
    setPriceSearch("");
  };

  const initPriceEdits = () => {
    if (priceListDetail && products) {
      const edits: Record<string, string> = {};
      for (const product of products) {
        const existing = priceListDetail.prices.find(p => p.productId === product.id);
        edits[product.id] = existing ? existing.price : "";
      }
      setPriceEdits(edits);
    }
  };

  const filteredProducts = (products || []).filter(p =>
    p.name.toLowerCase().includes(priceSearch.toLowerCase())
  );

  const currentPL = priceLists?.find(pl => pl.id === pricesDialogPL);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Prislistor</h1>
        <p className="text-muted-foreground">Hantera prislistor för olika kundgrupper</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5" />
              Prislistor
            </CardTitle>
            <CardDescription>
              Standard-prislistan visar ordinarie produktpriser. Skapa fler listor för specialpriser.
            </CardDescription>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="button-create-price-list">
            <Plus className="h-4 w-4 mr-2" />
            Ny prislista
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : priceLists && priceLists.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Skapad</TableHead>
                    <TableHead className="text-right">Åtgärder</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceLists.map(pl => (
                    <TableRow key={pl.id} data-testid={`row-price-list-${pl.id}`}>
                      <TableCell className="font-medium">{pl.name}</TableCell>
                      <TableCell>
                        {pl.isDefault ? (
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Standard</Badge>
                        ) : (
                          <Badge variant="outline">Anpassad</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(pl.createdAt!), "d MMM yyyy", { locale: sv })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPricesDialog(pl.id)}
                            data-testid={`button-edit-prices-${pl.id}`}
                          >
                            <Tags className="h-3.5 w-3.5 mr-1" />
                            Priser
                          </Button>
                          {!pl.isDefault && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => { setEditingPriceList(pl); setEditName(pl.name); }}
                                data-testid={`button-rename-${pl.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost" className="text-destructive" data-testid={`button-delete-${pl.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Radera prislista?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Alla priser i "{pl.name}" kommer att raderas. Kunder kopplade till denna prislista kommer att se standardpriser istället.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => deleteMutation.mutate(pl.id)}
                                    >
                                      Radera
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Tags className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga prislistor skapade ännu</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ny prislista</DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Namn</label>
            <Input
              placeholder="T.ex. Grossist, VIP..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              data-testid="input-new-price-list-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Avbryt</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
              data-testid="button-save-price-list"
            >
              {createMutation.isPending ? "Skapar..." : "Skapa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPriceList} onOpenChange={open => !open && setEditingPriceList(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Byt namn</DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Namn</label>
            <Input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              data-testid="input-edit-price-list-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPriceList(null)}>Avbryt</Button>
            <Button
              onClick={() => updateMutation.mutate()}
              disabled={!editName.trim() || updateMutation.isPending}
              data-testid="button-update-price-list-name"
            >
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pricesDialogPL}
        onOpenChange={open => {
          if (!open) { setPricesDialogPL(null); setPriceEdits({}); }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={() => initPriceEdits()}>
          <DialogHeader>
            <DialogTitle>
              Priser - {currentPL?.name}
              {currentPL?.isDefault && (
                <Badge variant="outline" className="ml-2 bg-blue-500/10 text-blue-600 border-blue-500/20">Standard</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {currentPL?.isDefault && (
              <p className="text-sm text-muted-foreground">
                Standard-prislistan visar produkternas grundpriser. Ändra priser direkt på produkten om du vill ändra standardpriset.
              </p>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök produkt..."
                value={priceSearch}
                onChange={e => setPriceSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-price-products"
              />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produkt</TableHead>
                  <TableHead className="text-right">Standardpris</TableHead>
                  {!currentPL?.isDefault && <TableHead className="text-right">Listpris</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map(product => {
                  const currentVal = priceEdits[product.id] ?? "";
                  return (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {product.unitPrice} kr
                      </TableCell>
                      {!currentPL?.isDefault && (
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={product.unitPrice}
                            value={currentVal}
                            onChange={e => setPriceEdits(prev => ({ ...prev, [product.id]: e.target.value }))}
                            className="w-28 ml-auto text-right"
                            data-testid={`input-price-${product.id}`}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {!currentPL?.isDefault && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPricesDialogPL(null); setPriceEdits({}); }}>Avbryt</Button>
              <Button
                onClick={() => savePricesMutation.mutate()}
                disabled={savePricesMutation.isPending}
                data-testid="button-save-prices"
              >
                <Save className="h-4 w-4 mr-2" />
                {savePricesMutation.isPending ? "Sparar..." : "Spara priser"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
